import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { SITE } from "../config/site";
import { SectionHeading, Reveal } from "./Reveal";

export const Stars = ({ rating = 5, className = "h-4 w-4" }) => (
  <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`${className} ${i < rating ? "fill-amber2 text-amber2" : "text-slate-600"}`}
      />
    ))}
  </div>
);

export function ReviewsSection({ limit, showHeading = true }) {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.getReviews().then((r) => setReviews(r.reviews)).catch(() => {});
  }, []);

  const shown = limit ? reviews.slice(0, limit) : reviews;

  return (
    <section data-testid="reviews-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
      {showHeading && (
        <SectionHeading
          eyebrow="Verified Customer Reviews"
          title="Drivers Who Skipped the Shop"
          sub="Real reviews from real brake jobs across Middlesex County and Southern New Hampshire."
        />
      )}

      <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {shown.map((r, i) => (
          <Reveal key={`${r.name}-${i}`} delay={(i % 3) * 0.08}>
            <article
              data-testid={`review-card-${i}`}
              className="flex h-full flex-col border border-edge bg-slate2 p-6"
            >
              <div className="flex items-center justify-between">
                <Stars rating={r.rating} />
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {r.source}
                </span>
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">“{r.text}”</p>
              <div className="mt-5 border-t border-edge pt-4">
                <p className="font-display text-lg font-bold uppercase text-slate-50">{r.name}</p>
                <p className="text-xs text-slate-400">
                  {r.town} · {r.service}
                </p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-10 flex flex-col gap-3 sm:flex-row">
        <a
          href={SITE.googleReviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="read-google-reviews-button"
          className="inline-flex min-h-[52px] items-center justify-center gap-2 border-2 border-edgehi px-7 font-display text-lg font-bold uppercase tracking-wide text-slate-100 transition-colors hover:border-amber2 hover:text-amber2"
        >
          Read Our Google Reviews <ExternalLink className="h-4 w-4" />
        </a>
        <a
          href={SITE.googleLeaveReviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="leave-review-button"
          className="inline-flex min-h-[52px] items-center justify-center gap-2 border-2 border-edgehi px-7 font-display text-lg font-bold uppercase tracking-wide text-slate-100 transition-colors hover:border-crimson hover:text-crimson"
        >
          Leave a Review <Star className="h-4 w-4" />
        </a>
      </Reveal>
    </section>
  );
}
